// Ratings.js
import React, { useState, useEffect } from "react";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import styles from "./Ratings.module.css";
import axios from "axios";
import { useArenaAuth } from "../../context/ArenaAuthContext";

const Ratings = () => {
  const [overallRating, setOverallRating] = useState(0);
  const [facilityRatings, setFacilityRatings] = useState([]);
  const [allRatings, setAllRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { arenaUser } = useArenaAuth();

  useEffect(() => {
    if (arenaUser?.arenaId) fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arenaUser?.arenaId]);

  const fetchRatings = async () => {
    try {
      setLoading(true);
      const arenaId = arenaUser?.arenaId;
      
      // Fetch overall arena ratings and facility ratings
      const ratingsResponse = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/arena-dashboard/detailed-ratings?arenaId=${arenaId}`
      );
      
      setOverallRating(ratingsResponse.data.overallRating);
      setFacilityRatings(ratingsResponse.data.facilityRatings);
      setAllRatings(ratingsResponse.data.allRatings);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching ratings:", error);
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars.push(<FaStar key={i} className={styles.star} />);
      } else if (i - 0.5 <= rating) {
        stars.push(<FaStarHalfAlt key={i} className={styles.star} />);
      } else {
        stars.push(<FaRegStar key={i} className={styles.star} />);
      }
    }
    return stars;
  };

  if (loading) {
    return (
      <div className={styles.ratings}>
        <h2 className={styles.title}>Arena Ratings</h2>
        <div className={styles.loading}>
          <p>Loading ratings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.ratings}>
      <h2 className={styles.title}>Arena Ratings & Reviews</h2>

      {/* Overall Arena Rating */}
      <div className={styles.overallRating}>
        <h3>Overall Arena Rating</h3>
        <div className={styles.ratingDisplay}>
          {renderStars(overallRating)}
          <span className={styles.ratingNumber}>
            {overallRating > 0 ? overallRating.toFixed(1) : 'No ratings yet'}
          </span>
        </div>
        <p className={styles.ratingNote}>
          Based on {allRatings.length} total review{allRatings.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Facility-wise Ratings */}
      {facilityRatings.length > 0 && (
        <div className={styles.facilityRatings}>
          <h3>Facility Ratings</h3>
          <div className={styles.facilityGrid}>
            {facilityRatings.map((facility) => (
              <div key={facility.facility_id} className={styles.facilityRating}>
                <h4>{facility.sport_name}</h4>
                <div className={styles.ratingDisplay}>
                  {renderStars(facility.avg_rating)}
                  <span className={styles.ratingNumber}>
                    {parseFloat(facility.avg_rating).toFixed(1)}
                  </span>
                </div>
                <p className={styles.facilityStats}>
                  {facility.total_ratings} review{facility.total_ratings !== 1 ? 's' : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All User Ratings */}
      <div className={styles.userRatingsSection}>
        <h3>All User Reviews</h3>
        {allRatings.length > 0 ? (
          <div className={styles.userRatings}>
            {allRatings.map((rating) => (
              <div key={rating.id} className={styles.userRating}>
                <div className={styles.ratingHeader}>
                  <h4>{rating.userName}</h4>
                  <div className={styles.ratingMeta}>
                    <div className={styles.ratingDisplay}>
                      {renderStars(rating.rating)}
                      <span className={styles.ratingNumber}>
                        {rating.rating.toFixed(1)}
                      </span>
                    </div>
                    {rating.sport_name && (
                      <span className={styles.facilityName}>
                        for {rating.sport_name}
                      </span>
                    )}
                  </div>
                </div>
                {rating.comment && (
                  <p className={styles.comment}>"{rating.comment}"</p>
                )}
                <div className={styles.ratingDate}>
                  {new Date(rating.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.noRatings}>No user reviews available yet.</p>
        )}
      </div>
    </div>
  );
};

export default Ratings;
